import moment from 'moment'
import { Hold } from 'app/models/hold-params'
import { HolidayCalendarParams } from 'app/models/holiday-calendar-params'
import { TradeListFacility, TradeListItem, TradeListSubTrade } from 'app/models/trade-list-params'
import {
    businessDaysDiffWithHolidays,
    filterByTradeDate,
    formatNumberToCurrency,
    formatPriceWithoutCurrency,
    stringToDate,
} from 'app/utils/util-functions'
import { getDisplayedSettlementDate, resolveTradeHolidayDates } from './master-trade-util'

/**
 * One Trade List table row: a `GET /api/trade-list` item in the vocabulary the
 * column definitions, the Sort By keys and the filters already use.
 *
 * The server does the joining, folding and the filter facts (holds, AA
 * statuses, allocation, unapplied, not-refreshed, stale); this adds only what
 * needs the browser's own data — the displayed settlement date and T+
 * (business date, holiday calendar) — and the currency-aware amount
 * formatting the cells print.
 *
 * Two names differ from the API on purpose, to keep `columnDefs` and
 * `tradeViewColumns` unchanged: `myEntity` here is the trade ACTION (Buy | Sell,
 * the API's `tradeAction`) and the entity names live in `entityName` /
 * `counterpartyName`, exactly as the stitched row always had them.
 */
export type TradeListRow = {
    id: string
    contractId: string
    /** systemId, or 'N/A' — the column has always shown the fallback. */
    tradeId: string
    systemId: string | null
    clientId: string | null
    /** Raw ledger status; `masterTradeStatusLabel` turns Settled into Closed. */
    status: string
    reason: string | null
    tradeDetails: string
    transferType: string | null
    paymentType: string | null
    tradeAssociation: string | null
    /** Buy | Sell */
    myEntity: string
    /** YYYY-MM-DD */
    tradeDate: string | null
    settlementDate: Date | undefined
    hasMultipleSDs: boolean
    tPlus: number
    entityName: string
    entityMei: string
    counterpartyName: string
    counterpartyMei: string
    counterpartyId: string | null
    buyerId: { id: string } | undefined
    sellerId: { id: string } | undefined
    dealId: { id: string } | undefined
    dealName: string
    dealCusip: string
    facilityIds: string[]
    facilityCount: number
    facilityName: string | null
    facilityCusip: string | null
    facilityStatusCount: number
    /** One CSV export row each, as the page always exported. */
    tradedFacilities: TradeListFacility[]
    facilityStatus: string | null
    agent: string
    /** Formatted for display; the Sort By dropdown has always sorted this string. */
    tradedAmount: string
    currentAmount: string
    tradedAmountRaw: number
    currentAmountRaw: number
    baseCurrency: string
    uniqueCurrencies: number
    price: number | null
    priceCount: number
    subTradeIds: string[]
    subTradeMyEntityIds: string[]
    subTradeCounterpartyIds: string[]
    dealAgentIds: string[]
    holds: Hold[]
    hasSignableDocs: boolean
    /** Every sub trade with its dates — what the Settlement Date filter reads. */
    subTrades: TradeListSubTrade[]
    aaStatuses: string[]
    hasUnappliedTransactions: boolean
    isFullyAllocated: boolean
    hasNotRefreshedDocuments: boolean
    hasStaleDocuments: boolean
}

export type TradeListRowContext = {
    businessDate: Date
    holidayCalendars: HolidayCalendarParams[]
}

const key = (id: string | null): { id: string } | undefined =>
    id ? { id } : undefined

/**
 * The Settlement Date filter, as it worked before the projection. A trade is
 * kept when any sub trade's displayed date — the locked date, or the target
 * date rolled forward by the business-day rule, i.e. what the SD cell shows —
 * is in the range. On top of that, an Open sub trade with a fully signed AA
 * is kept when the AA's target settlement date is in the range: the old page
 * fetched fully signed AAs by that date range and let them match too, so a
 * trade could be found by the date the agent set in the Signing Portal as
 * well as by the date the row shows.
 */
const hasMultipleDisplayedDates = (
    trade: Pick<TradeListRow, 'subTrades' | 'tradeAssociation'>,
    ctx: TradeListRowContext,
): boolean => {
    const shown = new Set<number>()
    for (const st of trade.subTrades) {
        if (st.lockedDate === null && st.targetSettlementDate === null) continue
        shown.add(
            getDisplayedSettlementDate(
                { lockedDate: st.lockedDate ?? undefined, targetSettlementDate: st.targetSettlementDate ?? '' },
                trade,
                ctx.businessDate,
                ctx.holidayCalendars,
            ).getTime(),
        )
    }
    return shown.size > 1
}

export const matchesSettlementDate = (
    range: [Date | null, Date | null] | [],
    trade: Pick<TradeListRow, 'subTrades' | 'tradeAssociation'>,
    ctx: TradeListRowContext,
): boolean =>
    trade.subTrades.some(st => {
        const displayed = getDisplayedSettlementDate(
            { lockedDate: st.lockedDate ?? undefined, targetSettlementDate: st.targetSettlementDate ?? '' },
            trade,
            ctx.businessDate,
            ctx.holidayCalendars,
        )
        if (filterByTradeDate(range, displayed)) return true
        return (
            st.status === 'Open' &&
            !!st.aaTargetSettlementDate &&
            filterByTradeDate(range, stringToDate(st.aaTargetSettlementDate))
        )
    })

export const buildTradeListRow = (
    item: TradeListItem,
    ctx: TradeListRowContext,
): TradeListRow => {
    const holidays = resolveTradeHolidayDates(item.tradeAssociation, ctx.holidayCalendars)
    const tPlusEnd = item.lockedDate ? moment(item.lockedDate) : moment(ctx.businessDate)
    const singleCurrency = item.currencyCount <= 1
    const formatAmount = (amount: number) =>
        singleCurrency
            ? formatNumberToCurrency(amount, item.baseCurrency)
            : formatPriceWithoutCurrency(amount, 2, 2)

    return {
        id: item.id,
        contractId: item.contractId,
        tradeId: item.tradeId ?? 'N/A',
        systemId: item.tradeId,
        clientId: item.internalId,
        status: item.status,
        reason: item.reason,
        tradeDetails: item.tradeDetails ?? '',
        transferType: item.transferType,
        paymentType: item.paymentType,
        tradeAssociation: item.tradeAssociation,
        myEntity: item.tradeAction ?? '',
        tradeDate: item.tradeDate,
        // Same rule as the expand panel, from the same function: the projection
        // gives the first sub trade's target and locked dates.
        settlementDate: item.targetSettlementDate === null && item.lockedDate === null
            ? undefined
            : getDisplayedSettlementDate(
                { lockedDate: item.lockedDate ?? undefined, targetSettlementDate: item.targetSettlementDate ?? '' },
                item,
                ctx.businessDate,
                ctx.holidayCalendars,
            ),
        // "Multiple" when the sub trades SHOW more than one date — after the
        // business-day roll, the rule the page always had: two targets that
        // both roll to the next business day are one date, not two. The
        // item's hasMultipleSds compares the stored dates and is not used.
        hasMultipleSDs: hasMultipleDisplayedDates(item, ctx),
        tPlus: businessDaysDiffWithHolidays(moment(item.tradeDate ?? undefined), tPlusEnd, holidays),
        entityName: item.myEntity ?? 'Unknown',
        entityMei: item.myEntityMei ?? 'N/A',
        counterpartyName: item.counterparty ?? 'Unknown',
        counterpartyMei: item.counterpartyMei ?? 'N/A',
        counterpartyId: item.counterpartyId,
        buyerId: key(item.buyerId),
        sellerId: key(item.sellerId),
        dealId: key(item.dealId),
        dealName: item.dealName ?? 'N/A',
        dealCusip: item.dealCusip ?? 'N/A',
        facilityIds: item.facilityIds,
        facilityCount: item.facilityCount,
        facilityName: item.facilityName,
        facilityCusip: item.facilityCusip,
        facilityStatusCount: item.facilityStatusCount,
        tradedFacilities: item.tradedFacilities ?? [],
        facilityStatus: item.facilityStatus,
        agent: item.agent ?? '',
        tradedAmount: formatAmount(item.tradedAmount),
        currentAmount: formatAmount(item.currentAmount),
        tradedAmountRaw: item.tradedAmount,
        currentAmountRaw: item.currentAmount,
        baseCurrency: item.baseCurrency,
        uniqueCurrencies: item.currencyCount,
        price: item.price,
        priceCount: item.priceCount,
        subTradeIds: item.subTradeIds,
        subTradeMyEntityIds: item.subTradeMyEntityIds,
        subTradeCounterpartyIds: item.subTradeCounterpartyIds,
        dealAgentIds: item.dealAgentIds,
        holds: item.holds,
        hasSignableDocs: item.hasSignableDocs,
        subTrades: item.subTrades,
        aaStatuses: item.aaStatuses,
        hasUnappliedTransactions: item.hasUnappliedTransactions,
        isFullyAllocated: item.isFullyAllocated,
        hasNotRefreshedDocuments: item.hasNotRefreshedDocuments,
        hasStaleDocuments: item.hasStaleDocuments,
    }
}

export const buildTradeListRows = (
    items: TradeListItem[],
    ctx: TradeListRowContext,
): TradeListRow[] => items.map(item => buildTradeListRow(item, ctx))

/** sub trade id → master trade id, from the projection alone (no sub-trade store). */
export const subTradeToMasterFromItems = (
    items: ReadonlyArray<Pick<TradeListItem, 'id' | 'subTradeIds'>>,
): Map<string, string> => {
    const map = new Map<string, string>()
    for (const item of items) {
        for (const subId of item.subTradeIds) map.set(subId, item.id)
    }
    return map
}
